import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { Col, Row } from "react-bootstrap";
import {
  getSession,
  handleError,
  requestSquematic,
} from "../constants/commonFunctionts";
import NuevoPedidoModal from "../components/NuevoPedidoModal";

const formatoCOP = new Intl.NumberFormat("es-CO", {
  style: "currency",
  currency: "COP",
  maximumFractionDigits: 0,
});

const ESTADOS_PEDIDO = {
  0: { label: "Pendiente", color: "#e2a03f" },
  1: { label: "Entregado", color: "#11ddb3" },
  2: { label: "Cancelado", color: "#dc3545" },
};

const parseConfPedido = (valor) => {
  if (Array.isArray(valor)) return valor;
  try {
    const parsed = JSON.parse(valor);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const formatoFechaLarga = new Intl.DateTimeFormat("es-CO", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

const formatoHora = new Intl.DateTimeFormat("es-CO", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const formatearFechaRecogida = (fecha) => {
  if (!fecha) return "";

  const date = new Date(fecha.includes("T") ? fecha : fecha.replace(" ", "T"));
  if (isNaN(date.getTime())) return fecha;

  const fechaTexto = formatoFechaLarga.format(date);
  const fechaCapitalizada =
    fechaTexto.charAt(0).toUpperCase() + fechaTexto.slice(1);

  return `${fechaCapitalizada} · ${formatoHora.format(date)}`;
};

export default function Dashboard() {
  const session = getSession();
  const [pedidos, setPedidos] = useState([]);
  const [cargandoPedidos, setCargandoPedidos] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [expandidos, setExpandidos] = useState({});

  useEffect(() => {
    if (session) {
      fetchPedidos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const fetchPedidos = async () => {
    setCargandoPedidos(true);
    try {
      const res = await requestSquematic(
        "GET",
        "/api/resuelve/listar_pedidos",
        {},
        session.token
      );

      if (res && res.results) {
        setPedidos(
          res.results.map((pedido) => ({
            ...pedido,
            c901_conf_pedido: parseConfPedido(pedido.c901_conf_pedido),
          }))
        );
      } else if (res && res.errors) {
        throw new Error(res.errors);
      }
    } catch (error) {
      handleError("Error obteniendo los pedidos", error);
    } finally {
      setCargandoPedidos(false);
    }
  };

  const handlePedidoGuardado = () => {
    setShowModal(false);
    fetchPedidos();
  };

  const toggleExpandido = (key) => {
    setExpandidos((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Box
      sx={{ minHeight: "100vh", backgroundColor: "#f5f7fb" }}
      pt={{ xs: 12, md: 14 }}
      pb={6}
      px={{ xs: 2, md: 6 }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "white",
          borderRadius: 3,
          padding: "20px 30px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
          flexWrap: "wrap",
          gap: 2,
        }}
        mb={4}
      >
        <Typography
          sx={{
            fontSize: { xs: 18, md: 22 },
            fontWeight: "bold",
            color: "#1555fd",
          }}
        >
          Bienvenido {session.nombre} {session.apellido}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 16, md: 18 },
            fontWeight: "bold",
            color: "#11ddb3",
          }}
        >
          Saldo: {formatoCOP.format(session.saldo || 0)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
        mb={3}
      >
        <Typography
          sx={{
            fontSize: { xs: 18, md: 20 },
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Pedidos ({pedidos.length})
          {cargandoPedidos && <CircularProgress size={16} color="inherit" />}
        </Typography>
        <button
          className="btn"
          style={{
            backgroundColor: "#11ddb3",
            color: "white",
            borderRadius: 8,
            fontWeight: "bold",
            padding: "10px 20px",
          }}
          onClick={() => setShowModal(true)}
        >
          + Añadir pedido
        </button>
      </Box>

      {pedidos.map((pedido, index) => {
        const estado = ESTADOS_PEDIDO[pedido.c901_estado_pedido] || {
          label: "Desconocido",
          color: "#797979",
        };
        const key = pedido.c901_rowid ?? index;
        const verMas = !!expandidos[key];

        return (
          <Box
            key={key}
            sx={{
              backgroundColor: "white",
              borderRadius: 3,
              padding: "24px",
              boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}
            mb={3}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
              mb={2}
            >
              <Typography sx={{ fontWeight: "bold", color: "#1555fd" }}>
                Pedido #{pedidos.length - index}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: "bold",
                    color: "white",
                    backgroundColor: estado.color,
                    borderRadius: 4,
                    padding: "2px 10px",
                  }}
                >
                  {estado.label}
                </Typography>
                <Typography
                  sx={{ fontSize: 14, fontWeight: "bold", color: "#1555fd" }}
                >
                  {formatoCOP.format(pedido.c901_valor || 0)}
                </Typography>
              </Box>
            </Box>

            <Row className="mb-2">
              <Col md={6}>
                <Typography sx={{ fontSize: 14, color: "#797979" }}>
                  <b>Recogida:</b> {pedido.c901_departamento_recogida},{" "}
                  {pedido.c901_ciudad_recogida}, {pedido.c901_barrio_recogida} -{" "}
                  {pedido.c901_dir_recogida}
                </Typography>
              </Col>
              <Col md={6}>
                <Typography sx={{ fontSize: 14, color: "#797979" }}>
                  <b>Entrega:</b> {pedido.c901_departamento_entrega},{" "}
                  {pedido.c901_ciudad_entrega}, {pedido.c901_barrio_entrega} -{" "}
                  {pedido.c901_dir_entrega}
                </Typography>
              </Col>
            </Row>
            <Row className="mb-2">
              <Col md={6}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: 14, color: "#797979" }}>
                    <b>Paquetes:</b> {pedido.c901_conf_pedido.length}
                  </Typography>
                  <button
                    className="btn"
                    style={{
                      backgroundColor: "transparent",
                      color: "#1555fd",
                      fontWeight: "bold",
                      fontSize: 13,
                      padding: "0 8px",
                      textDecoration: "underline",
                    }}
                    onClick={() => toggleExpandido(key)}
                  >
                    {verMas ? "Ver menos" : "Ver más"}
                  </button>
                </Box>
              </Col>
              <Col md={6}>
                <Typography sx={{ fontSize: 14, color: "#797979" }}>
                  <b>Fecha de recogida:</b>{" "}
                  {formatearFechaRecogida(pedido.c901_fecha_recogida)}
                </Typography>
              </Col>
            </Row>

            {verMas && (
              <Box
                component="ul"
                sx={{ margin: 0, marginBottom: 1, paddingLeft: "20px" }}
              >
                {pedido.c901_conf_pedido.map((paquete, i) => (
                  <Typography
                    key={i}
                    component="li"
                    sx={{ fontSize: 14, color: "#797979" }}
                  >
                    Paquete #{i + 1}: {paquete.tamano}
                    {paquete.delicado ? " · delicado" : ""}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        );
      })}

      <NuevoPedidoModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handlePedidoGuardado}
      />
    </Box>
  );
}
